/**
 * Pure view helpers for the Bexio reconciliation page.
 *
 * Everything here READS the reconciliation response and never recomputes a
 * financial fact from it. That distinction is the whole point: the Bexio mirror is
 * the leading source for invoiced / paid / open amounts, and the backend service
 * has already decided which invoices count, which are due, and what the sums are.
 * A second arithmetic in the frontend would be a second truth, and the two would
 * eventually disagree.
 *
 * So the operations below are strictly: SELECT rows by a field the backend set,
 * MATCH text against a label the backend sent, and FLATTEN a list the backend
 * nested. No sums, no date arithmetic, no re-derivation of due/paid/open.
 *
 * Money arrives as JSON strings (the response's Decimal fields serialize
 * losslessly that way), so comparisons go through `num`. Values are SIGNED — a
 * negative remaining is a possible overpayment and the service never clamps it,
 * so nothing here may either.
 */

/** A money/count field as a number, for comparison only — never for display. */
export function num(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * The KPI cards, each carrying the row predicate it stands for.
 *
 * Every predicate tests a field the backend put on the row, so a card's figure and
 * the rows its click reveals come from the same place. `all` is the reset.
 *
 * `paid` uses `paid_total_invoice_level`, which is invoice-level `total_paid`
 * summed over the counted documents — NOT payment rows, and NOT limited to a year.
 */
export const KPI_ROW_FILTERS = {
  all: () => true,
  open_due: (r) => num(r.open_due) !== 0,
  open_not_yet_due: (r) => num(r.open_not_yet_due_or_no_due_date) !== 0,
  paid: (r) => num(r.paid_total_invoice_level) !== 0,
};

export const KPI_KEYS = Object.keys(KPI_ROW_FILTERS);

/**
 * Exactly the Bexio statuses a counted document can carry.
 *
 * The reconciliation service counts `pending | partial | paid` only; `draft`,
 * `canceled` and `unknown` are excluded from every total and surfaced as warnings
 * instead. Offering the excluded ones in this filter would promise rows that, by
 * construction, cannot appear in the counted set.
 */
export const COUNTED_STATUSES = ["pending", "partial", "paid"];

/**
 * Tenancy ids the response flags somewhere.
 *
 * Built from the warning groups' own `tenancy_id` fields — the backend decides what
 * is worth flagging, this only collects the ids so a row can be marked. Not a
 * severity judgement and not a count: the counts stay in the warnings card, which
 * reads each group's own length.
 */
export function flaggedTenancyIds(warnings) {
  const ids = new Set();
  if (!warnings) return ids;
  const groups = [
    "canceled_with_payment_rows",
    "total_paid_differs_from_payment_sum",
    "paid_without_payment_rows",
    "pending_with_payment_rows",
    "excluded_mapped_invoices",
    "invalid_mapped_invoices",
    "duplicate_risk_vantio_invoices",
  ];
  for (const g of groups) {
    const list = warnings[g];
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (item?.tenancy_id) ids.add(String(item.tenancy_id));
    }
  }
  return ids;
}

/**
 * Does this row carry something to look at?
 *
 * Either the response flagged it in a warning group, or the row itself reports
 * mapped invoices that were excluded from the totals (draft / canceled / unknown
 * status) — both are the row's own fields, not an inference.
 */
export function rowHasHint(row, flaggedIds) {
  if (flaggedIds && flaggedIds.has(String(row.tenancy_id))) return true;
  return (
    num(row.mapped_draft_invoices) > 0 ||
    num(row.mapped_canceled_invoices) > 0 ||
    num(row.mapped_unknown_status_invoices) > 0 ||
    // The fourth exclusion reason, and the only one that is not a Bexio status.
    // Stated here rather than left to the flagged-ids path: a tenancy whose ONLY
    // excluded invoices are provider-missing would otherwise report "no hints" and
    // never render the line that explains where its money went.
    num(row.mapped_provider_missing_invoices) > 0
  );
}

/** The searchable identity of a tenancy row, as the backend labelled it. */
function tenancyHaystack(row) {
  return [
    row.tenancy_reference_number,
    row.tenant_display_name,
    row.unit_label,
    row.room_label,
  ];
}

function matches(haystack, term) {
  if (!term) return true;
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return haystack.some((v) =>
    v === null || v === undefined ? false : String(v).toLowerCase().includes(t)
  );
}

/** Does the row, or any of its counted documents, match the search term? */
export function tenancyMatchesSearch(row, term) {
  if (!term || !term.trim()) return true;
  if (matches(tenancyHaystack(row), term)) return true;
  const docs = Array.isArray(row.counted_documents) ? row.counted_documents : [];
  return docs.some((d) => matches([d.document_nr, d.bexio_invoice_id], term));
}

/**
 * Does the row hold a counted document with this Bexio status?
 *
 * A tenancy has no single status of its own — it holds several invoices — so a
 * status filter on this table means "has at least one counted invoice in that
 * status". The UI says so rather than implying the tenancy itself is "paid".
 */
export function tenancyHasStatus(row, status) {
  if (!status) return true;
  const docs = Array.isArray(row.counted_documents) ? row.counted_documents : [];
  return docs.some((d) => d.bexio_status === status);
}

/**
 * The tenancy rows to show, in the ORDER THE SERVER SENT THEM.
 *
 * The service already sorts by highest open total first (then reference, then id)
 * — deterministic and meaningful — so this filters without reordering. Re-sorting
 * here would quietly replace the backend's ranking with a client one.
 */
export function filterTenancyRows(
  tenancies,
  { kpi = "all", status = "", hint = "", q = "" } = {},
  flaggedIds
) {
  const rows = Array.isArray(tenancies) ? tenancies : [];
  const kpiPredicate = KPI_ROW_FILTERS[kpi] || KPI_ROW_FILTERS.all;
  return rows.filter((r) => {
    if (!kpiPredicate(r)) return false;
    if (!tenancyHasStatus(r, status)) return false;
    if (hint === "with" && !rowHasHint(r, flaggedIds)) return false;
    if (hint === "without" && rowHasHint(r, flaggedIds)) return false;
    if (!tenancyMatchesSearch(r, q)) return false;
    return true;
  });
}

/**
 * The counted invoices of the given rows, flattened, each keeping its tenancy.
 *
 * This is the answer to "which Bexio invoices actually went into the reconciliation"
 * — `counted_documents` IS that authoritative set, so no independent aggregation
 * happens here and no document can be counted twice: each one appears under the one
 * tenancy the backend nested it under.
 *
 * A status filter applies to the documents themselves here, which is stricter than
 * on the tenancy table (where it admits the whole row).
 */
export function countedDocumentRows(rows, { status = "", q = "" } = {}) {
  const out = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    const docs = Array.isArray(r.counted_documents) ? r.counted_documents : [];
    for (const d of docs) {
      if (status && d.bexio_status !== status) continue;
      if (
        q &&
        q.trim() &&
        !matches([d.document_nr, d.bexio_invoice_id, ...tenancyHaystack(r)], q)
      ) {
        continue;
      }
      out.push({
        // A stable key: `document_nr` can be absent, `bexio_invoice_id` cannot.
        key: `${r.tenancy_id}:${d.bexio_invoice_id}`,
        tenancy_id: r.tenancy_id,
        tenancy_reference_number: r.tenancy_reference_number,
        tenant_display_name: r.tenant_display_name,
        unit_label: r.unit_label,
        room_label: r.room_label,
        ...d,
      });
    }
  }
  return out;
}

/**
 * `mapped_quad` as a percentage of all mirrored invoices, or null.
 *
 * The only arithmetic in this module, and it is presentational: both operands come
 * from `mapping_coverage` and no money is involved. `invalid_mapped_or_unresolved`
 * is deliberately absent — it is a SUB-count of `mapped_quad`, so adding it
 * anywhere would double-count the same invoices.
 */
export function coveragePercent(coverage) {
  const total = num(coverage?.total);
  if (!total) return null;
  return Math.round((num(coverage?.mapped_quad) / total) * 100);
}
